// Reads behind a producer's view of WHERE the crew is: who shares their
// location, and when each person's phone last sent a fix. With the Supabase
// client as a parameter, shared by the producer web app and the PROduce
// Producer app.
//
// Both reads are "live crew" data (Lock B, PRO/STUDIO): the crew_locations
// RLS returns nothing to a LITE producer, so callers only ask when the plan
// allows it. A failed read THROWS, so React Query keeps the last answer
// instead of turning "could not ask" into "nobody is sharing".

import type { TrackingConsentSettings } from "./trackingConsent";

// deno-lint-ignore no-explicit-any
type Client = any; // kept loose so both web and mobile Supabase clients work

/** One crew member's two location switches, as get_crew_tracking_consent returns them. */
export interface CrewTrackingConsentRow extends TrackingConsentSettings {
  user_id: string;
  location_tracking_enabled: boolean;
  share_location_with_producer: boolean;
}

/**
 * Who on this production is sharing their location, and who switched it off.
 *
 * Goes through the get_crew_tracking_consent RPC rather than reading
 * crew_settings directly: it also answers for active co-producers, returns
 * the two consent booleans and nothing else, and answers for the whole
 * production in one call. Without it a crew list cannot tell "switched off"
 * from "phone is dead" — see crewTrackingStatus.
 */
export async function fetchCrewTrackingConsent(
  client: Client,
  briefingId: string,
): Promise<CrewTrackingConsentRow[]> {
  const { data, error } = await client.rpc("get_crew_tracking_consent", {
    p_briefing_id: briefingId,
  });
  if (error) throw error;
  return (data as CrewTrackingConsentRow[] | null) ?? [];
}

/** When one crew member's phone last sent an accepted fix. */
export interface LatestCrewFix {
  user_id: string;
  /** ISO timestamp of the fix. */
  timestamp: string;
}

/**
 * The most recent accepted fix per crew member on this production — the
 * `lastFixAt` that crewTrackingStatus needs.
 *
 * Reads the crew_locations_display view, which is contracted to one row per
 * (user, briefing) — the same source the web's live map uses, so both
 * surfaces call a person "Active" or "Stationary" from the same fix. Only the
 * two columns are selected; the positions are not needed for a status. A
 * duplicate row (should the contract ever break) collapses to the latest.
 */
export async function fetchLatestCrewFixes(
  client: Client,
  briefingId: string,
): Promise<LatestCrewFix[]> {
  const { data, error } = await client
    .from("crew_locations_display")
    .select("user_id, timestamp")
    .eq("briefing_id", briefingId);
  if (error) throw error;
  return latestFixPerUser((data as LatestCrewFix[] | null) ?? []);
}

/** One row per user, the newest fix winning; rows without a user or time are dropped. */
export function latestFixPerUser(rows: readonly LatestCrewFix[]): LatestCrewFix[] {
  const byUser = new Map<string, LatestCrewFix>();
  for (const row of rows) {
    if (!row?.user_id || !row.timestamp) continue;
    const seen = byUser.get(row.user_id);
    if (!seen || Date.parse(row.timestamp) > Date.parse(seen.timestamp)) {
      byUser.set(row.user_id, { user_id: row.user_id, timestamp: row.timestamp });
    }
  }
  return [...byUser.values()];
}
